import type React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui';

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
  color: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal';
  loading?: boolean;
  subtitle?: string;
}

const colorMap: Record<StatCardProps['color'], { icon: string; accent: string }> = {
  blue: { icon: 'text-blue-600 bg-blue-100', accent: 'border-l-blue-500' },
  amber: { icon: 'text-amber-600 bg-amber-100', accent: 'border-l-amber-500' },
  green: { icon: 'text-emerald-600 bg-emerald-100', accent: 'border-l-emerald-500' },
  purple: { icon: 'text-violet-600 bg-violet-100', accent: 'border-l-violet-500' },
  red: { icon: 'text-red-600 bg-red-100', accent: 'border-l-red-500' },
  teal: { icon: 'text-teal-600 bg-teal-100', accent: 'border-l-teal-500' },
};

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color, loading, subtitle }) => {
  const colors = colorMap[color];

  if (loading) {
    return (
      <Card padding="md" className={cn('border-l-4', colors.accent)}>
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-9 w-16" />
          </div>
          <Skeleton variant="rectangular" className="w-10 h-10 rounded-xl" />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="md" className={cn('border-l-4', colors.accent)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center shrink-0', colors.icon)}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
};

export default StatCard;
